import {
  Button,
  FileUploader,
  FormButtons,
  Header,
  TextField,
} from '@marekhealth/mhui';
import { useFormik, FormikProvider } from 'formik';
import useSavePhysicalExam from '@services/mutations/useSavePhysicalExam';
import { PhysicalExamInfoSchema } from '@schemas/home';
import { IPhysicalExamInfo, TSubmitFunction } from './types';
import * as S from './styles';
import SubmissionFeedback from '@components/SubmissionFeedback';
import { useState } from 'react';

export default function Home() {
  const {
    mutate: SavePhysicalExam,
    isError,
    isLoading,
    isSuccess,
    reset,
  } = useSavePhysicalExam();
  const [errorMessage, setErrorMessage] = useState('');

  const initialValues: IPhysicalExamInfo = {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    email: '',
    verifyEmail: '',
    files: [],
  };

  const handleSubmit: TSubmitFunction = (values, { resetForm }) => {
    const trimmedValues = PhysicalExamInfoSchema.cast(values) as typeof values;

    SavePhysicalExam(trimmedValues, {
      onSuccess: () => resetForm(),
      onError: error => {
        const response = error?.response?.data as { message?: string };
        setErrorMessage(response?.message ?? '');
      },
    });
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validationSchema: PhysicalExamInfoSchema,
  });

  return (
    <FormikProvider value={formik}>
      <S.Form onSubmit={formik.handleSubmit}>
        <S.Papper>
          <Header>Physical Exam Documents Upload Portal</Header>
          {(isError || isLoading || isSuccess) ? (
            <SubmissionFeedback
              isLoading={isLoading}
              isSuccess={isSuccess}
              errorMessage={errorMessage}
              onTryAgain={reset}
            />
          ): (
            <>
              <S.MedicalAppointmentImg />
              <S.Description>
                <S.DescriptionTitle>
                  Please use this form to upload your Physical Exam documentation.
                </S.DescriptionTitle>
                <S.DescriptionSubtitle>
                  To prevent delays in processing, please verify the following before uploading:
                </S.DescriptionSubtitle>
                <li>
                  Document is signed by the medical provider including their credentials
                  (NP, MD, DO, PA), practice location, and signature.
                </li>
                <li>
                  Document includes date of examination and is within the last 3 months.
                </li>
                <li>
                  Your information including name, DOB, and vitals are clearly listed within the document.
                </li>
              </S.Description>
              <S.FieldsWrapper>
                <TextField
                  name='firstName'
                  label='First Name'
                />
                <TextField
                  name='lastName'
                  label='Last Name'
                />
                <TextField
                  name='email'
                  label='Email'
                />
                <TextField
                  name='verifyEmail'
                  label='Verify your Email'
                />
                <TextField
                  name='dateOfBirth'
                  label='Date of Birth'
                  mask='date'
                />
                <FileUploader
                  name='files'
                  maxFiles={6}
                />
                <S.FormDescription>
                  If there are any issues with the documents provided,
                  our team will contact you via phone or email.
                </S.FormDescription>
                <FormButtons>
                  <Button type='submit'>
                    Submit
                  </Button>
                </FormButtons>
              </S.FieldsWrapper>
            </>
          )}
        </S.Papper>
      </S.Form>
    </FormikProvider>
  );
}
